import React, { useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../config/firebase';
import { collection, getDocs, query, orderBy, doc, updateDoc, addDoc } from 'firebase/firestore';
import Modal from 'react-modal';
import './admin.css';

const Admin = () => {
  const [users, setUsers] = useState([]);
  const [reports, setReports] = useState([]);
  const [stripeCustomers, setStripeCustomers] = useState([]);
  const [googlePlaySubscribers, setGooglePlaySubscribers] = useState([]);
  const [appleSubscribers, setAppleSubscribers] = useState([]);
  const [referrers, setReferrers] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [replyTicket, setReplyTicket] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [replySubject, setReplySubject] = useState('');
  const [isReplying, setIsReplying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [usersNextPageToken, setUsersNextPageToken] = useState(null);
  const [authDuplicates, setAuthDuplicates] = useState([]);
  const [firestoreDuplicates, setFirestoreDuplicates] = useState([]);
  const [hasCheckedDuplicates, setHasCheckedDuplicates] = useState(false);

  const [authNoEmail, setAuthNoEmail] = useState([]);
  const [firestoreNoEmail, setFirestoreNoEmail] = useState([]);
  const [hasCheckedNoEmail, setHasCheckedNoEmail] = useState(false);
  const [verifiedIncompleteUsers, setVerifiedIncompleteUsers] = useState([]);
  const [hasCheckedVerifiedIncomplete, setHasCheckedVerifiedIncomplete] = useState(false);
  const [incompleteCurrentPage, setIncompleteCurrentPage] = useState(1);
  const [incompletePageSize, setIncompletePageSize] = useState(10);
  const [viewingUserData, setViewingUserData] = useState(null);

  const fetchDuplicateAccounts = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const functions = getFunctions();
      const getDuplicates = httpsCallable(functions, 'getDuplicateAccounts');
      const result = await getDuplicates();
      setAuthDuplicates(result.data.authDuplicates || []);
      setFirestoreDuplicates(result.data.firestoreDuplicates || []);
      setHasCheckedDuplicates(true);
    } catch (err) {
      setError(err.message);
      console.error("Error fetching duplicate accounts:", err);
    }
    setIsLoading(false);
  };

  const fetchUsersWithoutEmail = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const functions = getFunctions();
      const getUsersNoEmailCall = httpsCallable(functions, 'getUsersWithoutEmail');
      const result = await getUsersNoEmailCall();
      setAuthNoEmail(result.data.authUsersWithoutEmail || []);
      setFirestoreNoEmail(result.data.firestoreUsersWithoutEmail || []);
      setHasCheckedNoEmail(true);
    } catch (err) {
      setError(err.message);
      console.error("Error fetching users without email:", err);
    }
    setIsLoading(false);
  };

  const fetchVerifiedIncompleteUsers = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const functions = getFunctions();
      const getVerifiedIncomplete = httpsCallable(functions, 'getVerifiedIncompleteUsers');
      const result = await getVerifiedIncomplete();
      setVerifiedIncompleteUsers(result.data.incompleteUsers || []);
      setIncompleteCurrentPage(1);
      setHasCheckedVerifiedIncomplete(true);
    } catch (err) {
      setError(err.message);
      console.error("Error fetching verified users with incomplete profiles:", err);
    }
    setIsLoading(false);
  };

  const fetchTickets = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const ticketsQuery = query(collection(db, 'supportTickets'), orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(ticketsQuery);
      const ticketList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setTickets(ticketList);
    } catch (err) {
      setError(err.message);
      console.error("Error fetching support tickets:", err);
    }
    setIsLoading(false);
  };

  const submitReply = async (ticket) => {
    if (!replyText.trim()) {
      alert("Please enter a reply message.");
      return;
    }
    const finalSubject = replySubject.trim() || `Re: [Flintt Support] - ${ticket.topic}`;
    setIsReplying(true);
    try {
      const newReply = {
        replyMessage: replyText.trim(),
        repliedAt: new Date().toISOString(),
        repliedBy: 'admin',
        replySubject: finalSubject
      };

      const currentReplies = Array.isArray(ticket.replies) ? ticket.replies : [];
      const updatedReplies = [...currentReplies, newReply];

      const ticketRef = doc(db, 'supportTickets', ticket.id);
      await updateDoc(ticketRef, {
        replies: updatedReplies,
        status: 'replied'
      });

      await addDoc(collection(db, 'mail'), {
        to: [ticket.email],
        message: {
          from: 'Flintt.me Support <support@flintt.me>',
          subject: finalSubject,
          html: `
            <div style="font-family: sans-serif; padding: 20px; max-width: 600px; border: 1px solid #fbead3; border-radius: 8px; color: #24252a;">
              <h2 style="color: #24252a; border-bottom: 2px solid #fbead3; padding-bottom: 10px; margin-top: 0;">Flintt Support Response</h2>
              <p>Hi ${ticket.name || 'there'},</p>
              <p>Our support team has responded to your query regarding <strong>"${ticket.topic}"</strong>.</p>
              
              <div style="background: #f9f9f9; padding: 15px; border-left: 5px solid #fbead3; margin: 20px 0; border-radius: 4px;">
                <p style="margin-top: 0; font-weight: bold; color: #555;">Support Team Response:</p>
                <p style="white-space: pre-wrap; color: #333; margin-bottom: 0;">${replyText.trim()}</p>
              </div>
              
              <hr style="border: 0; border-top: 1px solid #eee; margin: 20px 0;" />
              
              <div style="font-size: 0.9em; color: #666;">
                <p style="font-weight: bold; margin-top: 0;">Your Original Message:</p>
                <p style="white-space: pre-wrap; font-style: italic; margin-bottom: 0;">"${ticket.message}"</p>
              </div>
            </div>
          `
        }
      });

      alert("Reply sent successfully and ticket updated.");
      setReplyTicket(null);
      setReplyText('');
      setReplySubject('');
      fetchTickets();
    } catch (err) {
      alert("Error sending reply: " + err.message);
      console.error("Error replying to ticket:", err);
    }
    setIsReplying(false);
  };

  const formatTicketDate = (createdAt) => {
    if (!createdAt) return 'N/A';
    if (typeof createdAt.toMillis === 'function') {
      return new Date(createdAt.toMillis()).toLocaleString();
    }
    if (createdAt.seconds) {
      return new Date(createdAt.seconds * 1000).toLocaleString();
    }
    return new Date(createdAt).toLocaleString();
  };

  const fetchUsers = async (isLoadMore = false) => {
    setIsLoading(true);
    setError(null);
    try {
      const functions = getFunctions();
      const listAllUsersCall = httpsCallable(functions, 'listAllUsers');

      const payload = { maxResults: 10 };
      if (isLoadMore && usersNextPageToken) {
        payload.pageToken = usersNextPageToken;
      }

      const result = await listAllUsersCall(payload);

      if (isLoadMore) {
        setUsers(prev => {
          const existingUids = new Set(prev.map(u => u.uid));
          const newUsers = result.data.users.filter(u => !existingUids.has(u.uid));
          return [...prev, ...newUsers];
        });
      } else {
        setUsers(result.data.users);
      }
      setUsersNextPageToken(result.data.nextPageToken || null);

    } catch (err) {
      setError(err.message);
      console.error("Error fetching users:", err);
    }
    setIsLoading(false);
  };

  const fetchReferrers = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const functions = getFunctions();
      const listReferrers = httpsCallable(functions, 'listReferrers');
      const result = await listReferrers();
      setReferrers(result.data.referrers);
    } catch (err) {
      setError(err.message);
      console.error("Error fetching referrers:", err);
    }
    setIsLoading(false);
  };

  const fetchStripeCustomers = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const functions = getFunctions();
      const listStripeCustomers = httpsCallable(functions, 'listStripeCustomers');
      const result = await listStripeCustomers();
      setStripeCustomers(result.data.customers);
    } catch (err) {
      setError(err.message);
      console.error("Error fetching stripe customers:", err);
    }
    setIsLoading(false);
  };

  const fetchGooglePlaySubscribers = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const functions = getFunctions();
      const listGooglePlaySubscribers = httpsCallable(functions, 'listGooglePlaySubscribers');
      const result = await listGooglePlaySubscribers();
      setGooglePlaySubscribers(result.data.subscribers || []);
    } catch (err) {
      setError(err.message);
      console.error("Error fetching Google Play subscribers:", err);
    }
    setIsLoading(false);
  };

  const cancelGooglePlaySubscription = async (purchaseToken, subscriptionId) => {
    if (window.confirm('Are you sure you want to cancel this Google Play subscription?')) {
      setIsLoading(true);
      setError(null);
      try {
        const functions = getFunctions();
        const cancelOp = httpsCallable(functions, 'cancelGooglePlaySubscription');
        await cancelOp({ purchaseToken, subscriptionId });
        alert('Subscription cancelled successfully.');
        fetchGooglePlaySubscribers(); // refresh
      } catch (err) {
        setError(err.message);
        console.error("Error cancelling subscription:", err);
      }
      setIsLoading(false);
    }
  };

  const fetchAppleSubscribers = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const functions = getFunctions();
      const listAppleSubscribers = httpsCallable(functions, 'listAppleSubscribers');
      const result = await listAppleSubscribers();
      setAppleSubscribers(result.data.subscribers || []);
    } catch (err) {
      setError(err.message);
      console.error("Error fetching Apple subscribers:", err);
    }
    setIsLoading(false);
  };

  const toggleUserDisabled = async (uid, disabled) => {
    try {
      const functions = getFunctions();
      const disableUser = httpsCallable(functions, 'disableUser');
      await disableUser({ uid, disabled });
      fetchUsers();
    } catch (err) {
      setError(err.message);
      console.error("Error updating user:", err);
    }
  };

  const deleteUser = async (uid) => {
    if (window.confirm(`Are you sure you want to delete user ${uid}?`)) {
      try {
        const functions = getFunctions();
        const deleteUserCall = httpsCallable(functions, 'deleteUser');
        await deleteUserCall({ uid });
        fetchUsers();
      } catch (err) {
        setError(err.message);
        console.error("Error deleting user:", err);
      }
    }
  };

  const cleanupCorruptedAccounts = async () => {
    if (window.confirm('Are you sure you want to delete all corrupted (ghost) accounts from Firebase Authentication? This action cannot be undone.')) {
      setIsLoading(true);
      setError(null);
      try {
        const functions = getFunctions();
        const cleanupCall = httpsCallable(functions, 'cleanupCorruptedAccounts');
        const result = await cleanupCall();
        alert(result.data.message || 'Cleanup complete.');
        fetchUsers();
      } catch (err) {
        setError(err.message);
        console.error("Error cleaning up corrupted accounts:", err);
      }
      setIsLoading(false);
    }
  };

  const fetchReports = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const functions = getFunctions();
      const listReports = httpsCallable(functions, 'listReports');
      const result = await listReports();
      setReports(result.data.reports || []);
    } catch (err) {
      setError(err.message);
      console.error("Error fetching reports:", err);
    }
    setIsLoading(false);
  };

  return (
    <div className="main">
      <div className="content">
        <div className="admin-container">
          <h1>Admin Portal</h1>
          <button 
            className="admin-button" 
            onClick={cleanupCorruptedAccounts} 
            style={{ marginBottom: '20px', backgroundColor: '#e74c3c', color: 'white' }}
          >
            Cleanup All Corrupted Accounts
          </button>
          {isLoading && <p className="loading">Loading...</p>}
          {error && <p className="error">{error}</p>}
          
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <h2>Users</h2>
            <div>
              <button className="admin-button" onClick={() => fetchUsers(false)} style={{ marginRight: '10px' }}>Fetch</button>
              <button className="admin-button" onClick={() => fetchUsers(false)}>Refresh</button>
            </div>
          </div>
          <table className="admin-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>UID</th>
                <th>Email</th>
                <th>Status</th>
                <th>Last Login</th>
                <th>Premium Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.uid}>
                  <td>{user?.userData?.displayName ?? '-'}</td>
                  <td>{user.uid}</td>
                  <td>{user.email}</td>
                  <td>{user.disabled ? 'Disabled' : 'Active'}</td>
                  <td>{user.lastSignInTime ? new Date(user.lastSignInTime).toLocaleDateString() : '-'}</td>
                  <td>{user.isPremium ? 'Premium' : 'Free'}</td>
                  <td>
                    <button className="admin-button" onClick={() => toggleUserDisabled(user.uid, !user.disabled)}>
                      {user.disabled ? 'Enable' : 'Disable'}
                    </button>
                    <button className="admin-button" onClick={() => deleteUser(user.uid)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {usersNextPageToken && (
            <button className="admin-button" onClick={() => fetchUsers(true)} style={{ marginTop: '10px' }}>Load More</button>
          )}

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <h2>Reports</h2>
            <div>
              <button className="admin-button" onClick={fetchReports} style={{ marginRight: '10px' }}>Fetch</button>
              <button className="admin-button" onClick={fetchReports}>Refresh</button>
            </div>
          </div>
          <table className="admin-table">
            <thead>
              <tr>
                <th>Reported User Name</th>
                <th>Reported User</th>
                <th>Reporter Name</th>
                <th>Reporter</th>
                <th>Reason</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              {reports.map((report) => (
                <tr key={report.id}>
                  <td>{report?.reportedName ?? '-'}</td>
                  <td>{report?.reportedUID ?? '-'}</td>
                  <td>{report?.reporterName ?? '-'}</td>
                  <td>{report?.reporterUID ?? '-'}</td>
                  <td>{report?.reason ?? '-'}</td>
                  <td>{report?.notes ?? '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <h2>Stripe Customers</h2>
            <div>
              <button className="admin-button" onClick={fetchStripeCustomers} style={{ marginRight: '10px' }}>Fetch</button>
              <button className="admin-button" onClick={fetchStripeCustomers}>Refresh</button>
            </div>
          </div>
          <table className="admin-table">
            <thead>
              <tr>
                <th>Stripe ID</th>
                <th>Email</th>
                <th>Subscription Plan</th>
                <th>Subscription Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {stripeCustomers.map((customer) => (
                <tr key={customer.id}>
                  <td>{customer.id}</td>
                  <td>{customer.email}</td>
                  <td>{customer.subscription_plan}</td>
                  <td>{customer.subscription_date ? new Date(customer.subscription_date * 1000).toLocaleDateString() : 'N/A'}</td>
                  <td>
                    <a href={`https://dashboard.stripe.com/customers/${customer.id}`} target="_blank" rel="noopener noreferrer">
                      View in Stripe
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <h2>Google Play Subscriptions</h2>
            <div>
              <button className="admin-button" onClick={fetchGooglePlaySubscribers} style={{ marginRight: '10px' }}>Fetch</button>
              <button className="admin-button" onClick={fetchGooglePlaySubscribers}>Refresh</button>
            </div>
          </div>
          <table className="admin-table" style={{ tableLayout: 'auto' }}>
            <thead>
              <tr>
                <th>User Name</th>
                <th>User Email</th>
                <th>Purchase Token</th>
                <th>Subscription Plan</th>
                <th>Active</th>
                <th>Subscription Date</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {googlePlaySubscribers.map((sub, i) => (
                <tr key={i}>
                  <td>{sub.user_name}</td>
                  <td>{sub.user_email}</td>
                  <td style={{ wordBreak: 'break-all', maxWidth: '400px' }}>
                    {sub.purchaseToken}
                  </td>
                  <td>{sub.subscription_plan}</td>
                  <td>{sub.active ? 'True' : 'False'}</td>
                  <td>{sub.subscription_date ? new Date(sub.subscription_date).toLocaleDateString() : 'N/A'}</td>
                  <td>
                    {sub.active && (
                      <button
                        className="admin-button"
                        onClick={() => cancelGooglePlaySubscription(sub.purchaseToken, sub.subscription_plan)}
                      >
                        Cancel
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Apple App Store Subscriptions */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '40px' }}>
            <h2>Apple Subscriptions (App Store)</h2>
            <div>
              <button className="admin-button" onClick={fetchAppleSubscribers} style={{ marginRight: '10px' }}>Fetch</button>
              <button className="admin-button" onClick={fetchAppleSubscribers}>Refresh</button>
            </div>
          </div>
          <table className="admin-table" style={{ tableLayout: 'auto' }}>
            <thead>
              <tr>
                <th>User Name</th>
                <th>User Email</th>
                <th>Original Transaction ID</th>
                <th>Subscription Plan</th>
                <th>Status</th>
                <th>Environment</th>
                <th>Subscription Date</th>
                <th>Expires Date</th>
                <th>Ownership</th>
              </tr>
            </thead>
            <tbody>
              {appleSubscribers.length === 0 ? (
                <tr>
                  <td colSpan="9" style={{ textAlign: 'center', color: '#ccc', padding: '15px' }}>
                    No Apple App Store subscribers loaded. Click Fetch/Refresh.
                  </td>
                </tr>
              ) : (
                appleSubscribers.map((sub, i) => (
                  <tr key={i}>
                    <td><strong>{sub.user_name}</strong></td>
                    <td>{sub.user_email}</td>
                    <td style={{ wordBreak: 'break-all', maxWidth: '260px', fontFamily: 'monospace', fontSize: '0.9em' }}>
                      {sub.originalTransactionId}
                    </td>
                    <td>
                      <span style={{ textTransform: 'capitalize' }}>
                        {sub.subscription_plan ? sub.subscription_plan.replace('_', ' ') : 'N/A'}
                      </span>
                    </td>
                    <td>
                      <span className={sub.active ? 'sub-badge-active' : 'sub-badge-expired'}>
                        {sub.active ? 'Active' : (sub.status || 'Expired')}
                      </span>
                    </td>
                    <td>
                      <span className={sub.environment === 'Sandbox' ? 'sub-badge-sandbox' : 'sub-badge-prod'}>
                        {sub.environment || 'Production'}
                      </span>
                    </td>
                    <td>
                      {sub.subscription_date ? new Date(sub.subscription_date).toLocaleString() : 'N/A'}
                    </td>
                    <td>
                      {sub.expires_date ? new Date(sub.expires_date).toLocaleString() : 'N/A'}
                    </td>
                    <td>
                      <span style={{ fontSize: '0.85em', color: sub.ownership_status === 'superseded' ? '#ffc107' : '#28a745' }}>
                        {sub.ownership_status || (sub.active ? 'Active Owner' : 'Inactive')}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '40px' }}>
            <h2>Referrers</h2>
            <div>
              <button className="admin-button" onClick={fetchReferrers} style={{ marginRight: '10px' }}>Fetch</button>
              <button className="admin-button" onClick={fetchReferrers}>Refresh</button>
            </div>
          </div>
          <table className="admin-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Referral Link</th>
                <th>Conversions</th>
                <th>Onboarding Complete</th>
              </tr>
            </thead>
            <tbody>
              {referrers.map((referrer) => (
                <tr key={referrer.id}>
                  <td>{referrer.email}</td>
                  <td>{referrer.referralLink}</td>
                  <td>{referrer.conversions}</td>
                  <td>{referrer.onboardingEmailSent ? 'Yes' : 'No'}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '40px' }}>
            <h2>Support Tickets</h2>
            <div>
              <button className="admin-button" onClick={fetchTickets} style={{ marginRight: '10px' }}>Fetch</button>
              <button className="admin-button" onClick={fetchTickets}>Refresh</button>
            </div>
          </div>
          <table className="admin-table">
            <thead>
              <tr>
                <th>User / Email</th>
                <th>Topic</th>
                <th>Date</th>
                <th>Message</th>
                <th>Status</th>
                <th>Replies Count</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {tickets.length === 0 ? (
                <tr>
                  <td colSpan="7" style={{ textAlign: 'center', color: '#ccc' }}>No tickets loaded. Click Fetch/Refresh.</td>
                </tr>
              ) : (
                tickets.map((ticket) => (
                  <tr key={ticket.id}>
                    <td>
                      <div><strong>{ticket.name}</strong></div>
                      <div style={{ fontSize: '0.85em', color: '#ccc' }}>{ticket.email}</div>
                    </td>
                    <td>{ticket.topic}</td>
                    <td>{formatTicketDate(ticket.createdAt)}</td>
                    <td style={{ maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={ticket.message}>
                      {ticket.message}
                    </td>
                    <td style={{ color: ticket.status === 'open' ? '#ff5757' : '#28a745', fontWeight: 'bold' }}>
                      {ticket.status}
                    </td>
                    <td>{ticket.replies ? ticket.replies.length : 0}</td>
                    <td>
                      <button 
                        className="admin-button" 
                        onClick={() => {
                          setReplyTicket(ticket);
                          setReplyText('');
                          setReplySubject(`Re: [Flintt Support] - ${ticket.topic}`);
                        }}
                      >
                        Reply
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '40px' }}>
            <h2>Duplicate Accounts (Same Email)</h2>
            <div>
              <button className="admin-button" onClick={fetchDuplicateAccounts} style={{ marginRight: '10px' }}>
                Check Duplicates
              </button>
              <button className="admin-button" onClick={fetchDuplicateAccounts}>
                Refresh
              </button>
            </div>
          </div>

          {!hasCheckedDuplicates ? (
            <p style={{ color: '#ccc', fontStyle: 'italic', margin: '15px 0' }}>
              No duplicate email accounts loaded yet. Click "Check Duplicates" to scan both Firebase Auth and Firestore.
            </p>
          ) : authDuplicates.length === 0 && firestoreDuplicates.length === 0 ? (
            <p style={{ color: '#28a745', fontWeight: 'bold', margin: '15px 0' }}>
              🎉 No duplicate email accounts found in Firebase Authentication or Firestore!
            </p>
          ) : (
            <div style={{ marginTop: '15px' }}>
              {authDuplicates.length > 0 && (
                <div style={{ marginBottom: '30px' }}>
                  <h3 style={{ color: '#ff5757', borderBottom: '1px solid rgba(255, 87, 87, 0.3)', paddingBottom: '5px' }}>
                    ⚠️ Firebase Authentication: Duplicate Emails ({authDuplicates.length})
                  </h3>
                  {authDuplicates.map((group, idx) => (
                    <div 
                      key={`auth-${idx}`} 
                      style={{
                        border: '1px solid rgba(255, 87, 87, 0.3)',
                        borderRadius: '8px',
                        padding: '15px',
                        marginBottom: '15px',
                        backgroundColor: 'rgba(255, 87, 87, 0.05)'
                      }}
                    >
                      <h4 style={{ margin: '0 0 10px 0', color: '#FBEAD3' }}>
                        Email: <span style={{ color: '#ffffff' }}>{group.email}</span> ({group.count} Auth Accounts)
                      </h4>
                      <table className="admin-table" style={{ margin: 0 }}>
                        <thead>
                          <tr>
                            <th>Display Name</th>
                            <th>UID</th>
                            <th>Status</th>
                            <th>Created</th>
                            <th>Last Sign-In</th>
                            <th>Provider(s)</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {group.accounts.map((acc) => (
                            <tr key={acc.uid}>
                              <td>{acc.displayName || '-'}</td>
                              <td style={{ fontSize: '0.85em', fontFamily: 'monospace' }}>{acc.uid}</td>
                              <td>{acc.disabled ? 'Disabled' : 'Active'}</td>
                              <td>{acc.creationTime ? new Date(acc.creationTime).toLocaleDateString() : '-'}</td>
                              <td>{acc.lastSignInTime ? new Date(acc.lastSignInTime).toLocaleDateString() : '-'}</td>
                              <td>{acc.providerData ? acc.providerData.join(', ') : 'email'}</td>
                              <td>
                                <button className="admin-button" onClick={() => toggleUserDisabled(acc.uid, !acc.disabled)}>
                                  {acc.disabled ? 'Enable' : 'Disable'}
                                </button>
                                <button className="admin-button" onClick={() => deleteUser(acc.uid)}>
                                  Delete
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ))}
                </div>
              )}

              {firestoreDuplicates.length > 0 && (
                <div>
                  <h3 style={{ color: '#ffc107', borderBottom: '1px solid rgba(255, 193, 7, 0.3)', paddingBottom: '5px' }}>
                    ⚠️ Firestore Collection ("users"): Duplicate Emails ({firestoreDuplicates.length})
                  </h3>
                  {firestoreDuplicates.map((group, idx) => (
                    <div 
                      key={`db-${idx}`} 
                      style={{
                        border: '1px solid rgba(255, 193, 7, 0.3)',
                        borderRadius: '8px',
                        padding: '15px',
                        marginBottom: '15px',
                        backgroundColor: 'rgba(255, 193, 7, 0.05)'
                      }}
                    >
                      <h4 style={{ margin: '0 0 10px 0', color: '#FBEAD3' }}>
                        Email: <span style={{ color: '#ffffff' }}>{group.email}</span> ({group.count} Documents)
                      </h4>
                      <table className="admin-table" style={{ margin: 0 }}>
                        <thead>
                          <tr>
                            <th>Document ID</th>
                            <th>authUID Field</th>
                            <th>Display Name</th>
                            <th>Created</th>
                          </tr>
                        </thead>
                        <tbody>
                          {group.documents.map((docItem) => (
                            <tr key={docItem.id}>
                              <td style={{ fontSize: '0.85em', fontFamily: 'monospace' }}>{docItem.id}</td>
                              <td style={{ fontSize: '0.85em', fontFamily: 'monospace' }}>{docItem.authUID || '-'}</td>
                              <td>{docItem.displayName || '-'}</td>
                              <td>{docItem.createdAt ? new Date(docItem.createdAt).toLocaleDateString() : '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '40px' }}>
            <h2>Users Without Email</h2>
            <div>
              <button className="admin-button" onClick={fetchUsersWithoutEmail} style={{ marginRight: '10px' }}>
                Check Users Without Email
              </button>
              <button className="admin-button" onClick={fetchUsersWithoutEmail}>
                Refresh
              </button>
            </div>
          </div>

          {!hasCheckedNoEmail ? (
            <p style={{ color: '#ccc', fontStyle: 'italic', margin: '15px 0' }}>
              No check performed yet. Click "Check Users Without Email" to scan both Firebase Auth and Firestore.
            </p>
          ) : authNoEmail.length === 0 && firestoreNoEmail.length === 0 ? (
            <p style={{ color: '#28a745', fontWeight: 'bold', margin: '15px 0' }}>
              🎉 All users have email addresses!
            </p>
          ) : (
            <div style={{ marginTop: '15px' }}>
              {authNoEmail.length > 0 && (
                <div style={{ marginBottom: '30px' }}>
                  <h3 style={{ color: '#ff5757', borderBottom: '1px solid rgba(255, 87, 87, 0.3)', paddingBottom: '5px' }}>
                    ⚠️ Firebase Authentication: Missing Email ({authNoEmail.length})
                  </h3>
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Display Name</th>
                        <th>UID</th>
                        <th>Status</th>
                        <th>Created</th>
                        <th>Last Sign-In</th>
                        <th>Provider(s)</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {authNoEmail.map((acc) => (
                        <tr key={acc.uid}>
                          <td>{acc.displayName || '-'}</td>
                          <td style={{ fontSize: '0.85em', fontFamily: 'monospace' }}>{acc.uid}</td>
                          <td>{acc.disabled ? 'Disabled' : 'Active'}</td>
                          <td>{acc.creationTime ? new Date(acc.creationTime).toLocaleDateString() : '-'}</td>
                          <td>{acc.lastSignInTime ? new Date(acc.lastSignInTime).toLocaleDateString() : '-'}</td>
                          <td>{acc.providerData && acc.providerData.length > 0 ? acc.providerData.join(', ') : 'none'}</td>
                          <td>
                            <button className="admin-button" onClick={() => toggleUserDisabled(acc.uid, !acc.disabled)} style={{ marginRight: '5px' }}>
                              {acc.disabled ? 'Enable' : 'Disable'}
                            </button>
                            <button className="admin-button" onClick={() => deleteUser(acc.uid)} style={{ marginRight: '5px' }}>
                              Delete
                            </button>
                            <button className="admin-button" onClick={() => setViewingUserData(acc.authData || acc)}>
                              View Details
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {firestoreNoEmail.length > 0 && (
                <div>
                  <h3 style={{ color: '#ffc107', borderBottom: '1px solid rgba(255, 193, 7, 0.3)', paddingBottom: '5px' }}>
                    ⚠️ Firestore Collection ("users"): Missing Email ({firestoreNoEmail.length})
                  </h3>
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Document ID</th>
                        <th>authUID Field</th>
                        <th>Display Name</th>
                        <th>Created</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {firestoreNoEmail.map((docItem) => (
                        <tr key={docItem.id}>
                          <td style={{ fontSize: '0.85em', fontFamily: 'monospace' }}>{docItem.id}</td>
                          <td style={{ fontSize: '0.85em', fontFamily: 'monospace' }}>{docItem.authUID || '-'}</td>
                          <td>{docItem.displayName || '-'}</td>
                          <td>{docItem.createdAt ? new Date(docItem.createdAt).toLocaleDateString() : '-'}</td>
                          <td>
                            <button className="admin-button" onClick={() => setViewingUserData(docItem.userData || docItem)}>
                              View Details
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '40px' }}>
            <h2>Verified Email - Incomplete Profile Users</h2>
            <div>
              <button className="admin-button" onClick={fetchVerifiedIncompleteUsers} style={{ marginRight: '10px' }}>
                Check Incomplete Profiles
              </button>
              <button className="admin-button" onClick={fetchVerifiedIncompleteUsers}>
                Refresh
              </button>
            </div>
          </div>

          {!hasCheckedVerifiedIncomplete ? (
            <p style={{ color: '#ccc', fontStyle: 'italic', margin: '15px 0' }}>
              No check performed yet. Click "Check Incomplete Profiles" to scan users who verified their email but haven't set gender, pronouns, sexuality, or role.
            </p>
          ) : verifiedIncompleteUsers.length === 0 ? (
            <p style={{ color: '#28a745', fontWeight: 'bold', margin: '15px 0' }}>
              🎉 All users with verified emails have completed their profile!
            </p>
          ) : (
            <div style={{ marginTop: '15px' }}>
              <h3 style={{ color: '#ffc107', borderBottom: '1px solid rgba(255, 193, 7, 0.3)', paddingBottom: '5px' }}>
                ⚠️ Verified Email Users With Incomplete Profile ({verifiedIncompleteUsers.length})
              </h3>

              {(() => {
                const totalIncompletePages = Math.ceil(verifiedIncompleteUsers.length / incompletePageSize);
                const paginatedIncompleteUsers = verifiedIncompleteUsers.slice(
                  (incompleteCurrentPage - 1) * incompletePageSize,
                  incompleteCurrentPage * incompletePageSize
                );

                const renderPaginationControls = () => (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '10px', marginBottom: '15px', flexWrap: 'wrap', gap: '10px' }}>
                    <div style={{ fontSize: '0.9em', color: '#ccc' }}>
                      Showing {verifiedIncompleteUsers.length === 0 ? 0 : (incompleteCurrentPage - 1) * incompletePageSize + 1} to {Math.min(incompleteCurrentPage * incompletePageSize, verifiedIncompleteUsers.length)} of {verifiedIncompleteUsers.length} users
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <label style={{ fontSize: '0.85em', color: '#ccc' }}>Rows per page:</label>
                      <select
                        value={incompletePageSize}
                        onChange={(e) => {
                          setIncompletePageSize(Number(e.target.value));
                          setIncompleteCurrentPage(1);
                        }}
                        style={{
                          backgroundColor: '#24252A',
                          color: '#FBEAD3',
                          border: '1px solid rgba(251, 234, 211, 0.3)',
                          borderRadius: '4px',
                          padding: '3px 8px',
                          fontFamily: 'inherit'
                        }}
                      >
                        <option value={5}>5</option>
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                      </select>

                      <button
                        className="admin-button"
                        disabled={incompleteCurrentPage === 1}
                        onClick={() => setIncompleteCurrentPage(1)}
                        style={{ opacity: incompleteCurrentPage === 1 ? 0.4 : 1, cursor: incompleteCurrentPage === 1 ? 'not-allowed' : 'pointer' }}
                      >
                        «
                      </button>
                      <button
                        className="admin-button"
                        disabled={incompleteCurrentPage === 1}
                        onClick={() => setIncompleteCurrentPage(prev => Math.max(1, prev - 1))}
                        style={{ opacity: incompleteCurrentPage === 1 ? 0.4 : 1, cursor: incompleteCurrentPage === 1 ? 'not-allowed' : 'pointer' }}
                      >
                        ‹
                      </button>

                      <span style={{ fontSize: '0.85em', color: '#FBEAD3', margin: '0 5px' }}>
                        Page {incompleteCurrentPage} of {totalIncompletePages || 1}
                      </span>

                      <button
                        className="admin-button"
                        disabled={incompleteCurrentPage >= totalIncompletePages}
                        onClick={() => setIncompleteCurrentPage(prev => Math.min(totalIncompletePages, prev + 1))}
                        style={{ opacity: incompleteCurrentPage >= totalIncompletePages ? 0.4 : 1, cursor: incompleteCurrentPage >= totalIncompletePages ? 'not-allowed' : 'pointer' }}
                      >
                        ›
                      </button>
                      <button
                        className="admin-button"
                        disabled={incompleteCurrentPage >= totalIncompletePages}
                        onClick={() => setIncompleteCurrentPage(totalIncompletePages)}
                        style={{ opacity: incompleteCurrentPage >= totalIncompletePages ? 0.4 : 1, cursor: incompleteCurrentPage >= totalIncompletePages ? 'not-allowed' : 'pointer' }}
                      >
                        »
                      </button>
                    </div>
                  </div>
                );

                return (
                  <>
                    {renderPaginationControls()}
                    <table className="admin-table">
                      <thead>
                        <tr>
                          <th>UID</th>
                          <th>Email</th>
                          <th>Display Name</th>
                          <th>Profile Fields Status</th>
                          <th>Created</th>
                          <th>Last Sign-In</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedIncompleteUsers.map((user) => (
                          <tr key={user.uid}>
                            <td style={{ fontSize: '0.85em', fontFamily: 'monospace' }}>{user.uid}</td>
                            <td>{user.email || '-'}</td>
                            <td>{user.displayName || '-'}</td>
                            <td style={{ fontSize: '0.85em' }}>
                              <div><strong>Gender:</strong> {user.gender ? <span className="present-field-badge">{user.gender}</span> : <span className="missing-field-badge">Null</span>}</div>
                              <div><strong>Pronouns:</strong> {user.pronouns ? <span className="present-field-badge">{user.pronouns}</span> : <span className="missing-field-badge">Null</span>}</div>
                              <div><strong>Sexuality:</strong> {user.sexuality ? <span className="present-field-badge">{user.sexuality}</span> : <span className="missing-field-badge">Null</span>}</div>
                              <div><strong>Role:</strong> {user.role ? <span className="present-field-badge">{user.role}</span> : <span className="missing-field-badge">Null</span>}</div>
                              <div><strong>Interests:</strong> {user.interests && user.interests.length > 0 ? <span className="present-field-badge">{user.interests.length} added</span> : <span className="missing-field-badge">Null</span>}</div>
                              <div><strong>Bio:</strong> {user.bio ? <span className="present-field-badge">Set</span> : <span className="missing-field-badge">Null</span>}</div>
                              <div><strong>Location Pref:</strong> {user.locationPref && (Array.isArray(user.locationPref) ? user.locationPref.length > 0 : !!user.locationPref) ? <span className="present-field-badge">{Array.isArray(user.locationPref) ? user.locationPref.join(', ') : String(user.locationPref)}</span> : <span className="missing-field-badge">Null</span>}</div>
                              <div><strong>Photo:</strong> {user.displayPhoto ? <span className="present-field-badge">Uploaded</span> : <span className="missing-field-badge">Null</span>}</div>
                              <div><strong>Location:</strong> {user.location ? <span className="present-field-badge">Set</span> : <span className="missing-field-badge">Null</span>}</div>
                            </td>
                            <td style={{ fontSize: '0.85em' }}>{user.creationTime ? new Date(user.creationTime).toLocaleString() : '-'}</td>
                            <td style={{ fontSize: '0.85em' }}>{user.lastSignInTime ? new Date(user.lastSignInTime).toLocaleString() : '-'}</td>
                            <td>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', minWidth: '100px' }}>
                                <button className="admin-button" onClick={() => toggleUserDisabled(user.uid, !user.disabled)} style={{ margin: 0, width: '100%' }}>
                                  {user.disabled ? 'Enable' : 'Disable'}
                                </button>
                                <button className="admin-button" onClick={() => deleteUser(user.uid)} style={{ margin: 0, width: '100%' }}>
                                  Delete
                                </button>
                                <button className="admin-button" onClick={() => setViewingUserData(user.userData || user)} style={{ margin: 0, width: '100%' }}>
                                  View Details
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {renderPaginationControls()}
                  </>
                );
              })()}
            </div>
          )}
        </div>
      </div>

      {replyTicket && (
        <Modal
          className="support-reply-modal"
          isOpen={!!replyTicket}
          onRequestClose={() => setReplyTicket(null)}
          contentLabel="Reply to Support Ticket"
          ariaHideApp={false}
        >
          <h2 style={{ borderBottom: '1px solid rgba(251, 234, 211, 0.2)', paddingBottom: '12px' }}>Reply to Support Ticket</h2>
          <div style={{ color: '#ffffff', maxHeight: '400px', overflowY: 'auto', marginBottom: '20px', textAlign: 'left' }}>
            <p style={{ margin: '8px 0' }}><strong>From:</strong> <span style={{ color: '#FBEAD3' }}>{replyTicket.name} ({replyTicket.email})</span></p>
            <p style={{ margin: '8px 0' }}><strong>Topic:</strong> <span style={{ color: '#FBEAD3' }}>{replyTicket.topic}</span></p>
            <p style={{ margin: '16px 0 8px 0' }}><strong>Message:</strong></p>
            <blockquote style={{ background: 'rgba(255, 255, 255, 0.05)', padding: '12px 16px', borderLeft: '4px solid #FBEAD3', whiteSpace: 'pre-wrap', margin: '0 0 15px 0', borderRadius: '4px', color: '#ffffff', fontFamily: 'inherit' }}>
               {replyTicket.message}
            </blockquote>
            
            {replyTicket.replies && replyTicket.replies.length > 0 && (
               <div style={{ marginBottom: '15px' }}>
                 <p style={{ fontWeight: 'bold', margin: '16px 0 8px 0' }}>Previous Replies:</p>
                 {replyTicket.replies.map((r, i) => (
                   <div key={i} style={{ borderBottom: '1px dashed rgba(255, 255, 255, 0.1)', paddingBottom: '8px', marginBottom: '8px' }}>
                     <p style={{ margin: '0 0 4px 0', fontSize: '0.85em', color: '#FBEAD3' }}>
                       Replied on {new Date(r.repliedAt).toLocaleString()} by {r.repliedBy}:
                     </p>
                     <p style={{ margin: 0, whiteSpace: 'pre-wrap', color: '#ffffff' }}>{r.replyMessage}</p>
                   </div>
                 ))}
               </div>
            )}
            
            <label htmlFor="replySubject" style={{ fontWeight: 'bold', display: 'block', margin: '16px 0 8px 0', color: '#FBEAD3' }}>
              Subject:
            </label>
            <input
              id="replySubject"
              type="text"
              value={replySubject}
              onChange={(e) => setReplySubject(e.target.value)}
              placeholder="Email subject..."
              style={{
                width: '100%',
                padding: '12px',
                borderRadius: '8px',
                border: '1px solid rgba(251, 234, 211, 0.3)',
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                color: '#ffffff',
                boxSizing: 'border-box',
                fontFamily: 'inherit',
                fontSize: '0.95rem',
                marginBottom: '10px'
              }}
              disabled={isReplying}
            />

            <label htmlFor="replyText" style={{ fontWeight: 'bold', display: 'block', margin: '16px 0 8px 0', color: '#FBEAD3' }}>
              Your Response:
            </label>
            <textarea
              id="replyText"
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              placeholder="Type your response here..."
              style={{
                width: '100%',
                minHeight: '120px',
                padding: '12px',
                borderRadius: '8px',
                border: '1px solid rgba(251, 234, 211, 0.3)',
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                color: '#ffffff',
                boxSizing: 'border-box',
                fontFamily: 'inherit',
                fontSize: '0.95rem'
              }}
              disabled={isReplying}
            />
          </div>
          <div className="reply-actions" style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
            <button 
              onClick={() => setReplyTicket(null)} 
              disabled={isReplying}
              className="cancel-btn"
            >
              Cancel
            </button>
            <button
              onClick={() => submitReply(replyTicket)}
              className="submit-btn"
              disabled={isReplying}
            >
              {isReplying ? "Sending..." : "Send Reply"}
            </button>
          </div>
        </Modal>
      )}

      {viewingUserData && (
        <Modal
          className="support-reply-modal"
          isOpen={!!viewingUserData}
          onRequestClose={() => setViewingUserData(null)}
          contentLabel="View Full User Details"
          ariaHideApp={false}
        >
          <h2 style={{ borderBottom: '1px solid rgba(251, 234, 211, 0.2)', paddingBottom: '12px' }}>Full User Data Record</h2>
          <div style={{ color: '#ffffff', maxHeight: '400px', overflowY: 'auto', marginBottom: '20px', textAlign: 'left' }}>
            <pre style={{
              background: 'rgba(0, 0, 0, 0.3)',
              padding: '15px',
              borderRadius: '8px',
              border: '1px solid rgba(251, 234, 211, 0.2)',
              overflowX: 'auto',
              fontFamily: 'monospace',
              fontSize: '0.9em',
              color: '#FBEAD3',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-all'
            }}>
              {JSON.stringify(viewingUserData, null, 2)}
            </pre>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
            <button 
              onClick={() => setViewingUserData(null)} 
              className="submit-btn"
            >
              Close
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default Admin;
